import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSwipeable } from 'react-swipeable';
import { Link, useNavigate } from 'react-router-dom';
import { configService } from '../services/ConfigService';
import Footer from '../components/Footer';

function Home() {
  const [currentBoothIndex, setCurrentBoothIndex] = useState(0);
  const [booths, setBooths] = useState<any[]>([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [direction, setDirection] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const loadBooths = async () => {
      // تحميل البيانات الحقيقية في الخلفية
      let attempts = 0;
      const maxAttempts = 10;

      while (attempts < maxAttempts) {
        attempts++;

        // انتظار تهيئة ConfigService
        if (!configService.isInitialized()) {
          await new Promise(resolve => setTimeout(resolve, 300));
          continue;
        }

        const config = configService.getConfig();

        // التحقق من وجود البيانات
        if (config.booths && config.booths.length > 0) {
          const allBooths = config.booths
            .filter(booth => booth.slug && booth.name) // تصفية البوثات الصحيحة فقط
            .map((booth, index) => ({
              id: index + 1,
              name: booth.name,
              image: booth.imageUrl || (booth.slug === 'classic' ? '/images/Booth-Classic.png' : '/images/Booth-Modern.png'),
              description: booth.description,
              slug: booth.slug
            }));

          if (allBooths.length > 0) {
            console.log('✅ Successfully loaded booths, setting display:', allBooths);
            setBooths(allBooths);
            return;
          }
        }

        // إعادة محاولة تحميل البيانات من API
        if (attempts === 3 || attempts === 6) {
          try {
            await configService.refreshConfig();
          } catch (error) {
            console.error('Failed to refresh config:', error);
          }
        }

        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // في حالة فشل التحميل، استخدام القيم الافتراضية
      console.warn('⚠️ Using fallback booth configuration');
      setBooths([
        {
          id: 1,
          name: 'كابينة Classic',
          image: '/images/Booth-Classic.png',
          description: 'كابينة التصوير الكلاسيكية المثالية للمناسبات الراقية',
          slug: 'classic'
        },
        {
          id: 2,
          name: 'كابينة Modern',
          image: '/images/Booth-Modern.png',
          description: 'كابينة عصرية مع أحدث التقنيات للحصول على صور مذهلة',
          slug: 'modern'
        }
      ]);
    };

    loadBooths();
  }, []);

  const handleNext = () => {
    if (booths.length > 0) {
      setDirection(-1);
      setCurrentBoothIndex((prevIndex) => (prevIndex + 1) % booths.length);
    }
  };

  const handlePrev = () => {
    if (booths.length > 0) {
      setDirection(1);
      setCurrentBoothIndex((prevIndex) => (prevIndex - 1 + booths.length) % booths.length);
    }
  };

  const currentBooth = booths[currentBoothIndex];

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => handlePrev(),  // في RTL، السحب لليسار = السابق
    onSwipedRight: () => handleNext(), // في RTL، السحب لليمين = التالي
    trackMouse: true
  });

  // إذا لم تكن البوثات محملة بعد، عرض شاشة فارغة مؤقتة
  if (!currentBooth || booths.length === 0) {
    return (
      <div className="App home-page">
        <nav className="navbar">
          <div className="nav-container">
            <div className="logo">
              <img src="/images/logo.png" alt="Fotomista" />
            </div>
            <ul className="nav-menu">
              <li><a href="#home">الرئيسية</a></li>
              <li><Link to="/contact">اتصل بنا</Link></li>
            </ul>
          </div>
        </nav>
        <section className="hero"></section>
      </div>
    );
  }

  const handleDimensionsClick = () => {
    navigate(`/dimensions/${currentBooth.slug}`);
  };

  const handleBookingClick = () => {
    setIsTransitioning(true);
    // Wait for animation to complete before navigating
    setTimeout(() => {
      navigate(`/booking/${currentBooth.slug}`);
    }, 800);
  };

  // الكود سيعرض البوثات فوراً بدون شاشات تحميل

  return (
    <div className="App home-page">
      <nav className="navbar">
        <div className="nav-container">
          <div className="logo">
            <img src="/images/logo.png" alt="Fotomista" />
          </div>
          <ul className="nav-menu">
            <li><a href="#home">الرئيسية</a></li>
            <li><Link to="/contact">اتصل بنا</Link></li>
          </ul>
        </div>
      </nav>

      <section className="hero">
        <motion.div
          className="hero-content"
          animate={{
            opacity: isTransitioning ? 0 : 1
          }}
          transition={{ duration: 0.4 }}
        >
          <div className="hero-text">
            <h1>اختر Photo Booth لمناسبتك:</h1>
          </div>

          <div className="hero-image" {...swipeHandlers}>
            <button className="arrow-button arrow-left" onClick={handlePrev}>
              &#8250;
            </button>

            <div className="carousel-container">
              <AnimatePresence initial={false} custom={direction}>
                <motion.div
                  key={currentBooth.id}
                  custom={direction}
                  variants={{
                    enter: (direction: number) => {
                      return {
                        x: direction > 0 ? -1000 : 1000,
                        opacity: 0
                      };
                    },
                    center: {
                      zIndex: 1,
                      x: 0,
                      opacity: 1
                    },
                    exit: (direction: number) => {
                      return {
                        zIndex: 0,
                        x: direction < 0 ? -1000 : 1000,
                        opacity: 0
                      };
                    }
                  }}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    x: { type: "spring", stiffness: 300, damping: 30 },
                    opacity: { duration: 0.2 }
                  }}
                  className="booth-display"
                >
                  <div className="booth-placeholder">
                    <div className="booth-image-container">
                      <img
                        src={currentBooth.image}
                        alt={currentBooth.name}
                        className="booth-image"
                      />
                    </div>
                    <div className="booth-info">
                      <h3>{currentBooth.name}</h3>
                      <p>{currentBooth.description}</p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            <button className="arrow-button arrow-right" onClick={handleNext}>
              &#8249;
            </button>
          </div>
          <button className="dimensions-button" onClick={handleDimensionsClick}>
            المقاسات ونموذج 3D
          </button>
        </motion.div>

        <div className="arch-container">
          <div className="arch">
            <button className="next-button" onClick={handleBookingClick}>
              احجز هذا البوث
            </button>
          </div>
        </div>

        {/* Transition overlay */}
        <AnimatePresence>
          {isTransitioning && (
            <motion.div
              className="transition-overlay"
              initial={{
                clipPath: 'ellipse(0% 0% at 50% 100%)',
                opacity: 0
              }}
              animate={{
                clipPath: 'ellipse(150% 150% at 50% 100%)',
                opacity: 1
              }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                zIndex: 999 // أقل من z-index للـ navbar (1000)
              }}
            />
          )}
        </AnimatePresence>
      </section>

      <Footer />
    </div>
  );
}

export default Home;